<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex overflow-hidden">

    <AppSidebar currentPage="clients" />

    <main class="flex-1 flex flex-col h-full overflow-hidden relative">

      <!-- Top Header -->
      <header class="h-16 bg-surface border-b border-slate-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0 z-10">
        <button @click="goToClientList"
          class="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium">
          <span class="material-symbols-outlined" style="font-size: 20px;">arrow_back</span>
          Back to Client List
        </button>

      </header>

      <!-- Loading -->
      <div v-if="loading" class="flex-1 flex items-center justify-center">
        <div class="text-center">
          <svg class="animate-spin h-12 w-12 text-primary mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-slate-500">Loading client details...</p>
        </div>
      </div>

      <!-- Content -->
      <div v-else class="flex-1 overflow-y-auto bg-background-light p-4 md:p-8">
        <div class="max-w-[1400px] mx-auto space-y-6">

          <!-- Client Header Card -->
          <div class="bg-surface rounded-xl border border-slate-200 shadow-sm p-4 md:p-6">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div class="flex items-center gap-5">
                <div class="h-16 w-16 rounded-xl flex items-center justify-center text-xl font-bold bg-blue-100 text-blue-700 flex-shrink-0">
                  {{ client.initials }}
                </div>
                <div>
                  <div class="flex flex-wrap items-center gap-2 mb-1">
                    <h2 class="text-xl md:text-2xl font-bold text-slate-900 leading-tight">{{ client.name }}</h2>
                    <div class="relative">
                      <button @click.stop="showStatusMenu = !showStatusMenu"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity"
                        :class="client.statusClass">
                        <span class="h-1.5 w-1.5 rounded-full" :class="client.statusDotClass"></span>
                        {{ client.status }}
                        <span class="material-symbols-outlined" style="font-size: 12px;">expand_more</span>
                      </button>
                      <div v-if="showStatusMenu"
                        class="absolute left-0 top-8 bg-white rounded-xl shadow-xl border border-slate-200 py-1 w-48 z-20">
                        <button v-for="s in statusOptions" :key="s" @click="updateStatus(s)"
                          class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition-colors flex items-center gap-2">
                          <span class="h-2 w-2 rounded-full" :class="getStatusDotClass(s)"></span>
                          {{ s }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                    <span class="flex items-center gap-1">
                      <span class="material-symbols-outlined" style="font-size: 16px;">tag</span>
                      {{ client.id }}
                    </span>
                    <span class="flex items-center gap-1">
                      <span class="material-symbols-outlined" style="font-size: 16px;">business</span>
                      {{ client.entityType }}
                    </span>
                    <span class="flex items-center gap-1">
                      <span class="material-symbols-outlined" style="font-size: 16px;">person</span>
                      CPA: {{ client.cpaName }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex flex-wrap gap-2 flex-shrink-0">
                <button @click="openSendMessage"
                  class="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-all flex items-center gap-1.5">
                  <span class="material-symbols-outlined" style="font-size: 16px;">chat</span>
                  Send Message
                </button>
                <button @click="openInviteModal"
                  class="px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-semibold hover:bg-indigo-100 border border-indigo-200 transition-all flex items-center gap-1.5">
                  <span class="material-symbols-outlined" style="font-size: 16px;">mail</span>
                  Send Portal Invite
                </button>
                <button @click="openAssignTask"
                  class="px-3 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover shadow-md shadow-primary/20 transition-all flex items-center gap-1.5">
                  <span class="material-symbols-outlined" style="font-size: 16px;">add_task</span>
                  Assign Task
                </button>
              </div>
            </div>
          </div>

          <!-- Info Cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="bg-surface rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 class="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <span class="material-symbols-outlined text-primary" style="font-size: 18px;">contact_mail</span>
                Contact Information
              </h3>
              <div class="space-y-3">
                <div class="flex items-center gap-3 group">
                  <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">mail</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-slate-400">Email</p>
                    <div v-if="editingField === 'email'" class="flex items-center gap-1.5 mt-0.5">
                      <input v-model="fieldDraft" type="email" :disabled="savingField"
                        @keyup.enter="saveField('email', 'email')" @keyup.esc="cancelFieldEdit"
                        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                      <button @click="saveField('email', 'email')" :disabled="savingField" title="Save"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">check</span>
                      </button>
                      <button @click="cancelFieldEdit" :disabled="savingField" title="Cancel"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                      </button>
                    </div>
                    <p v-else class="text-sm text-slate-700 font-medium">{{ client.email || 'N/A' }}</p>
                  </div>
                  <button v-if="editingField !== 'email'" @click="startFieldEdit('email', client.email)"
                    title="Edit Email"
                    class="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-primary flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size: 16px;">edit</span>
                  </button>
                </div>
                <div class="flex items-center gap-3 group">
                  <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">phone</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-slate-400">Phone</p>
                    <div v-if="editingField === 'phone'" class="flex items-center gap-1.5 mt-0.5">
                      <input v-model="fieldDraft" type="text" :disabled="savingField"
                        @keyup.enter="saveField('phone', 'phone')" @keyup.esc="cancelFieldEdit"
                        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                      <button @click="saveField('phone', 'phone')" :disabled="savingField" title="Save"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">check</span>
                      </button>
                      <button @click="cancelFieldEdit" :disabled="savingField" title="Cancel"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                      </button>
                    </div>
                    <p v-else class="text-sm text-slate-700 font-medium">{{ client.phone || 'N/A' }}</p>
                  </div>
                  <button v-if="editingField !== 'phone'" @click="startFieldEdit('phone', client.phone)"
                    title="Edit Phone"
                    class="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-primary flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size: 16px;">edit</span>
                  </button>
                </div>
                <div class="flex items-center gap-3 group">
                  <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">location_on</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-slate-400">Address</p>
                    <div v-if="editingField === 'address'" class="flex items-center gap-1.5 mt-0.5">
                      <input v-model="fieldDraft" type="text" :disabled="savingField"
                        @keyup.enter="saveField('address', 'address')" @keyup.esc="cancelFieldEdit"
                        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                      <button @click="saveField('address', 'address')" :disabled="savingField" title="Save"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">check</span>
                      </button>
                      <button @click="cancelFieldEdit" :disabled="savingField" title="Cancel"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                      </button>
                    </div>
                    <p v-else class="text-sm text-slate-700 font-medium">{{ client.address || 'N/A' }}</p>
                  </div>
                  <button v-if="editingField !== 'address'" @click="startFieldEdit('address', client.address)"
                    title="Edit Address"
                    class="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-primary flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size: 16px;">edit</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="bg-surface rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 class="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <span class="material-symbols-outlined text-primary" style="font-size: 18px;">receipt_long</span>
                Tax Information
              </h3>
              <div class="space-y-3">
                <div class="flex items-center gap-3 group">
                  <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">numbers</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-slate-400">EIN / SSN</p>
                    <div v-if="editingField === 'taxId'" class="flex items-center gap-1.5 mt-0.5">
                      <input v-model="fieldDraft" type="text" :disabled="savingField"
                        @keyup.enter="saveField('taxId', 'taxId')" @keyup.esc="cancelFieldEdit"
                        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                      <button @click="saveField('taxId', 'taxId')" :disabled="savingField" title="Save"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">check</span>
                      </button>
                      <button @click="cancelFieldEdit" :disabled="savingField" title="Cancel"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                      </button>
                    </div>
                    <p v-else class="text-sm text-slate-700 font-medium">{{ client.taxId || 'N/A' }}</p>
                  </div>
                  <button v-if="editingField !== 'taxId'" @click="startFieldEdit('taxId', client.taxId)"
                    title="Edit EIN / SSN"
                    class="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-primary flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size: 16px;">edit</span>
                  </button>
                </div>
                <div class="flex items-center gap-3 group">
                  <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">business</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-slate-400">Entity Type</p>
                    <div v-if="editingField === 'entityType'" class="flex items-center gap-1.5 mt-0.5">
                      <input v-model="fieldDraft" type="text" list="entityTypeListInline"
                        :disabled="savingField" @keyup.enter="saveField('entityType', 'serviceType')" @keyup.esc="cancelFieldEdit"
                        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                      <datalist id="entityTypeListInline">
                        <option v-for="t in entityTypeOptions" :key="t" :value="t"></option>
                      </datalist>
                      <button @click="saveField('entityType', 'serviceType')" :disabled="savingField" title="Save"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">check</span>
                      </button>
                      <button @click="cancelFieldEdit" :disabled="savingField" title="Cancel"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                      </button>
                    </div>
                    <p v-else class="text-sm text-slate-700 font-medium">{{ client.entityType || 'N/A' }}</p>
                  </div>
                  <button v-if="editingField !== 'entityType'" @click="startFieldEdit('entityType', client.entityType)"
                    title="Edit Entity Type"
                    class="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-primary flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size: 16px;">edit</span>
                  </button>
                </div>
                <div class="flex items-center gap-3 group">
                  <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">calendar_today</span>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-slate-400">Tax Year</p>
                    <div v-if="editingField === 'taxYear'" class="flex items-center gap-1.5 mt-0.5">
                      <select v-model="fieldDraft" :disabled="savingField"
                        @keyup.enter="saveField('taxYear', 'taxYear')" @keyup.esc="cancelFieldEdit"
                        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
                        <option v-for="y in taxYearOptions" :key="y" :value="y">{{ y }}</option>
                      </select>
                      <button @click="saveField('taxYear', 'taxYear')" :disabled="savingField" title="Save"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">check</span>
                      </button>
                      <button @click="cancelFieldEdit" :disabled="savingField" title="Cancel"
                        class="text-slate-400 hover:text-primary disabled:opacity-50 flex-shrink-0">
                        <span class="material-symbols-outlined" style="font-size: 16px;">close</span>
                      </button>
                    </div>
                    <p v-else class="text-sm text-slate-700 font-medium">{{ client.taxYear || currentTaxYear }}</p>
                  </div>
                  <button v-if="editingField !== 'taxYear'" @click="startFieldEdit('taxYear', client.taxYear)"
                    title="Edit Tax Year"
                    class="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-primary flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size: 16px;">edit</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="bg-surface rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 class="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <span class="material-symbols-outlined text-primary" style="font-size: 18px;">bar_chart</span>
                Activity Summary
              </h3>
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-slate-500">Total Tasks</span>
                  <span class="text-sm font-bold text-slate-800">{{ tasks.length }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-slate-500">Completed</span>
                  <span class="text-sm font-bold text-green-600">{{ tasks.filter(t => t.status === 'Completed').length }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-slate-500">In Progress</span>
                  <span class="text-sm font-bold text-blue-600">{{ tasks.filter(t => t.status === 'In Progress').length }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-slate-500">Last Active</span>
                  <span class="text-sm font-bold text-slate-800">{{ client.lastActive }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabs -->
          <div class="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div class="flex border-b border-slate-200 bg-white px-4 md:px-6 overflow-x-auto scrollbar-none">
              <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
                class="px-3 py-4 text-sm font-medium border-b-2 transition-colors mr-1 flex-shrink-0 whitespace-nowrap"
                :class="activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'">
                <span class="flex items-center gap-2">
                  <span class="material-symbols-outlined" style="font-size: 18px;">{{ tab.icon }}</span>
                  {{ tab.label }}
                  <span v-if="tab.id === 'messages' && messages.length > 0"
                    class="px-1.5 py-0.5 rounded-full bg-primary text-secondary text-xs font-bold">
                    {{ messages.length }}
                  </span>
                </span>
              </button>
            </div>

            <div class="p-4 md:p-6">

              <!-- OVERVIEW TAB -->
              <div v-if="activeTab === 'overview'">
                <div class="flex items-center justify-between mb-6">
                  <h3 class="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <span class="material-symbols-outlined text-primary" style="font-size: 20px;">person</span>
                    Client Overview
                  </h3>
                  <button @click="openEditClient"
                    class="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-all flex items-center gap-2">
                    <span class="material-symbols-outlined" style="font-size: 18px;">edit</span>
                    Edit Info
                  </button>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-4">
                    <div class="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p class="text-xs text-slate-400 mb-1">Full Name / Company</p>
                      <p class="text-sm font-semibold text-slate-800">{{ client.name }}</p>
                    </div>
                    <div class="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p class="text-xs text-slate-400 mb-1">Email Address</p>
                      <p class="text-sm font-semibold text-slate-800">{{ client.email || 'N/A' }}</p>
                    </div>
                    <div class="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p class="text-xs text-slate-400 mb-1">Phone Number</p>
                      <p class="text-sm font-semibold text-slate-800">{{ client.phone || 'N/A' }}</p>
                    </div>
                  </div>
                  <div class="space-y-4">
                    <div class="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p class="text-xs text-slate-400 mb-1">Entity Type</p>
                      <p class="text-sm font-semibold text-slate-800">{{ client.entityType || 'N/A' }}</p>
                    </div>
                    <div class="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p class="text-xs text-slate-400 mb-1">Assigned CPA</p>
                      <p class="text-sm font-semibold text-slate-800">{{ client.cpaName || 'N/A' }}</p>
                    </div>
                    <div class="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p class="text-xs text-slate-400 mb-1">Current Status</p>
                      <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border" :class="client.statusClass">
                        <span class="h-1.5 w-1.5 rounded-full" :class="client.statusDotClass"></span>
                        {{ client.status }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- TASKS TAB -->
              <div v-if="activeTab === 'tasks'">
                <div class="flex items-center justify-between mb-6">
                  <h3 class="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <span class="material-symbols-outlined text-primary" style="font-size: 20px;">playlist_add_check</span>
                    Active Tasks
                  </h3>
                  <div class="flex flex-wrap gap-2">
                    <button @click="openAssignTemplate"
                      class="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-all flex items-center gap-2">
                      <span class="material-symbols-outlined" style="font-size: 18px;">playlist_add</span>
                      Assign Template
                    </button>
                    <button @click="openAssignTask"
                      class="px-3 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center gap-2">
                      <span class="material-symbols-outlined" style="font-size: 18px;">add_task</span>
                      Add Task
                    </button>
                  </div>
                </div>

                <div class="overflow-x-auto rounded-lg border border-slate-200">
                  <table class="w-full text-left border-collapse">
                    <thead>
                      <tr class="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider font-semibold border-b border-slate-200">
                        <th class="px-5 py-3">Task Name</th>
                        <th class="px-5 py-3">Status</th>
                        <th class="px-5 py-3">Due Date</th>
                        <th class="px-5 py-3">Assignee</th>
                        <th class="px-5 py-3">Priority</th>
                        <th class="px-5 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100">
                      <tr v-if="tasks.length === 0">
                        <td colspan="6" class="px-5 py-10 text-center">
                          <span class="material-symbols-outlined text-slate-300" style="font-size: 40px;">task</span>
                          <p class="text-slate-400 text-sm mt-2">No tasks yet for this client</p>
                        </td>
                      </tr>
                      <tr
                        v-for="task in tasks"
                        :key="task.id"
                        class="transition-colors group"
                        :class="task.status === 'Completed'
                          ? 'bg-slate-50/50 opacity-60 hover:opacity-100'
                          : 'hover:bg-slate-50/50'">

                        <!-- Task Name -->
                        <td class="px-5 py-4">
                          <div class="flex items-center gap-2">
                            <div v-if="task.status === 'Completed'"
                              class="p-1 rounded-full bg-green-100 text-green-600 flex-shrink-0">
                              <span class="material-symbols-outlined" style="font-size: 16px;">check</span>
                            </div>
                            <span v-else
                              class="material-symbols-outlined text-slate-400"
                              style="font-size: 18px;">
                              {{ task.icon }}
                            </span>
                            <div>
                              <p class="text-sm font-medium"
                                :class="task.status === 'Completed'
                                  ? 'text-slate-400 line-through decoration-slate-300'
                                  : 'text-slate-900'">
                                {{ task.name }}
                              </p>
                              <p class="text-xs text-slate-400">{{ task.category }}</p>
                            </div>
                          </div>
                        </td>

                        <!-- Status -->
                        <td class="px-5 py-4">
                          <span v-if="task.status === 'Completed'"
                            class="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">
                            Done
                          </span>
                          <span v-else
                            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                            :class="getTaskStatusClass(task.status)">
                            <span class="h-1.5 w-1.5 rounded-full" :class="getTaskStatusDotClass(task.status)"></span>
                            {{ task.status }}
                          </span>
                        </td>

                        <!-- Due Date -->
                        <td class="px-5 py-4">
                          <span class="text-sm"
                            :class="task.status !== 'Completed' && isOverdue(task.rawDueDate)
                              ? 'text-red-600 font-medium'
                              : 'text-slate-400'">
                            {{ task.dueDate }}
                            <span v-if="task.status !== 'Completed' && isOverdue(task.rawDueDate)" class="text-xs ml-1">(Overdue)</span>
                          </span>
                        </td>

                        <!-- Assignee -->
                        <td class="px-5 py-4">
                          <div class="flex items-center gap-2">
                            <div class="h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold bg-primary/10 text-primary">
                              {{ task.assigneeInitials }}
                            </div>
                            <span class="text-sm text-slate-600">{{ task.assignee }}</span>
                          </div>
                        </td>

                        <!-- Priority -->
                        <td class="px-5 py-4">
                          <span v-if="task.status !== 'Completed'"
                            class="inline-flex items-center gap-1 text-xs font-medium"
                            :class="getPriorityClass(task.priority)">
                            <span class="material-symbols-outlined" style="font-size: 14px;">flag</span>
                            {{ task.priority }}
                          </span>
                          <span v-else class="text-xs text-slate-300">—</span>
                        </td>

                        <!-- Actions -->
                        <td class="px-5 py-4 text-right">
                          <div class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              v-if="task.status !== 'Completed'"
                              @click="markTaskComplete(task)"
                              :disabled="task._completing"
                              class="p-1.5 text-slate-400 hover:text-green-600 transition-colors rounded-lg hover:bg-green-50 disabled:opacity-40"
                              title="Mark Complete">
                              <svg v-if="task._completing" class="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span v-else class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>
                            </button>
                            <button
                              @click="deleteTask(task)"
                              class="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                              title="Delete">
                              <span class="material-symbols-outlined" style="font-size: 16px;">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Quick Add Task -->
                <div class="mt-6 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <h4 class="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <span class="material-symbols-outlined text-primary" style="font-size: 18px;">add_task</span>
                    Quickly Add a Single Task
                  </h4>
                  <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input v-model="newTask.name" type="text" placeholder="Task name"
                      class="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white" />
                    <select v-model="newTask.priority"
                      class="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                      <option value="">Priority</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Normal">Normal</option>
                    </select>
                    <input v-model="newTask.dueDate" type="date"
                      class="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white" />
                    <button @click="quickAddTask" :disabled="addingTask"
                      class="px-4 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                      <svg v-if="addingTask" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span v-else class="material-symbols-outlined" style="font-size: 18px;">add</span>
                      {{ addingTask ? 'Adding...' : 'Add Task' }}
                    </button>
                  </div>
                  <p v-if="quickAddError" class="text-xs text-red-500 mt-2">{{ quickAddError }}</p>
                  <p v-if="quickAddSuccess" class="text-xs text-green-600 mt-2">{{ quickAddSuccess }}</p>
                </div>
              </div>

              <!-- MESSAGES TAB -->
              <div v-if="activeTab === 'messages'">
                <div class="flex items-center justify-between mb-6">
                  <h3 class="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <span class="material-symbols-outlined text-primary" style="font-size: 20px;">chat</span>
                    Messages
                  </h3>
                  <button @click="openSendMessage"
                    class="px-3 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center gap-2">
                    <span class="material-symbols-outlined" style="font-size: 18px;">send</span>
                    Send Message
                  </button>
                </div>
                <div v-if="loadingMessages" class="flex justify-center py-10">
                  <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <div v-else-if="messages.length === 0" class="text-center py-12 text-slate-400">
                  <span class="material-symbols-outlined text-slate-300" style="font-size: 48px;">chat</span>
                  <p class="mt-2 text-sm">No messages yet</p>
                  <button @click="openSendMessage" class="mt-3 text-sm text-primary hover:underline">Send the first message</button>
                </div>
                <div v-else class="space-y-3">
                  <div v-for="msg in messages" :key="msg.id"
                    class="flex gap-4 p-4 rounded-xl border border-slate-200 hover:border-primary/30 transition-colors">
                    <div class="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                      {{ msg.senderInitials }}
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center justify-between mb-1">
                        <div>
                          <p class="text-sm font-semibold text-slate-800">{{ msg.subject }}</p>
                          <p class="text-xs text-slate-400">From: {{ msg.sender }}</p>
                        </div>
                        <span class="text-xs text-slate-400">{{ msg.date }}</span>
                      </div>
                      <p class="text-sm text-slate-600">{{ msg.content }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- DOCUMENTS TAB -->
              <div v-if="activeTab === 'documents'">
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                  <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto scrollbar-none">
                    <button
                      v-for="folder in folderNames"
                      :key="folder"
                      @click="activeFolder = folder"
                      class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                      :class="activeFolder === folder ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
                      {{ folder }}
                      <span
                        v-if="folderStructure[folder] && folderStructure[folder].length"
                        class="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold"
                        :class="activeFolder === folder ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-500'">
                        {{ folderStructure[folder].length }}
                      </span>
                    </button>
                  </div>
                  <button @click="openUploadModal"
                    class="px-3 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center gap-2">
                    <span class="material-symbols-outlined" style="font-size: 18px;">upload</span>
                    Upload Document
                  </button>
                </div>

                <div v-if="loadingDocs" class="flex justify-center py-10">
                  <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>

                <div v-else-if="currentFolderFiles.length === 0"
                  class="text-center py-14 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <span class="material-symbols-outlined text-slate-300" style="font-size: 48px;">folder_open</span>
                  <p class="text-slate-500 mt-3 font-medium text-sm">No documents in "{{ activeFolder }}"</p>
                  <button @click="openUploadModal" class="mt-3 text-sm text-primary hover:underline">
                    Upload the first document here
                  </button>
                </div>

                <div v-else class="rounded-xl border border-slate-200 overflow-hidden">
                  <table class="w-full text-left">
                    <thead>
                      <tr class="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                        <th class="px-5 py-3">File Name</th>
                        <th class="px-5 py-3">Size</th>
                        <th class="px-5 py-3">Uploaded</th>
                        <th class="px-5 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100">
                      <tr v-for="doc in currentFolderFiles" :key="doc.FileUrl"
                        class="hover:bg-slate-50 transition-colors group">
                        <td class="px-5 py-3">
                          <div class="flex items-center gap-3">
                            <span class="material-symbols-outlined" :class="getDocIconClass(getFileType(doc.Name))" style="font-size: 22px;">
                              {{ getDocIcon(getFileType(doc.Name)) }}
                            </span>
                            <span class="text-sm font-medium text-slate-800 truncate max-w-[260px]">{{ doc.Name }}</span>
                          </div>
                        </td>
                        <td class="px-5 py-3 text-sm text-slate-500">{{ formatFileSize(doc.Size) }}</td>
                        <td class="px-5 py-3 text-sm text-slate-500">{{ formatDate(doc.Created) }}</td>
                        <td class="px-5 py-3 text-right">
                          <div class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a :href="buildFileUrl(doc)"
                              target="_blank"
                              class="p-1.5 text-slate-400 hover:text-primary transition-colors rounded-lg hover:bg-primary/10 inline-flex"
                              title="Download">
                              <span class="material-symbols-outlined" style="font-size: 18px;">download</span>
                            </a>
                            <button @click="deleteDocument(doc)"
                              class="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                              title="Delete">
                              <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </main>

    <!-- SEND MESSAGE MODAL -->
    <div v-if="showMessageModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">send</span>
            Message to {{ client.name }}
          </h3>
          <button @click="showMessageModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Subject</label>
            <input v-model="messageForm.subject" type="text" placeholder="Message subject..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message</label>
            <textarea v-model="messageForm.message" rows="4" placeholder="Write your message here..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"></textarea>
          </div>
          <div class="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <p class="text-sm font-medium text-slate-700">Also send via email</p>
              <p class="text-xs text-slate-400">Send email notification to client</p>
            </div>
            <button @click="messageForm.sendEmail = !messageForm.sendEmail"
              class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
              :class="messageForm.sendEmail ? 'bg-primary' : 'bg-slate-300'">
              <span class="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                :class="messageForm.sendEmail ? 'translate-x-6' : 'translate-x-1'"></span>
            </button>
          </div>
          <div v-if="messageError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">error</span>{{ messageError }}
          </div>
          <div v-if="messageSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>{{ messageSuccess }}
          </div>
          <div class="flex gap-3 pt-2">
            <button @click="showMessageModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button @click="sendMessage" :disabled="sendingMessage"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="sendingMessage" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ sendingMessage ? 'Sending...' : 'Send Message' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ASSIGN TASK MODAL -->
    <div v-if="showAssignTaskModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">add_task</span>
            Assign Task to {{ client.name }}
          </h3>
          <button @click="showAssignTaskModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Task Title *</label>
            <input v-model="taskForm.title" type="text" placeholder="e.g. Review Draft Return"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
            <textarea v-model="taskForm.description" rows="3" placeholder="Optional task description..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"></textarea>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Category</label>
              <select v-model="taskForm.category"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                <option value="Tax Preparation">Tax Preparation</option>
                <option value="Document Collection">Document Collection</option>
                <option value="Compliance">Compliance</option>
                <option value="Accounting">Accounting</option>
                <option value="Review">Review</option>
                <option value="Filing">Filing</option>
                <option value="Signature">Signature</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Priority</label>
              <select v-model="taskForm.priority"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Normal">Normal</option>
              </select>
            </div>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Due Date</label>
            <input v-model="taskForm.dueDate" type="date"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div v-if="taskError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">error</span>{{ taskError }}
          </div>
          <div v-if="taskSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>{{ taskSuccess }}
          </div>
          <div class="flex gap-3 pt-2">
            <button @click="showAssignTaskModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button @click="assignTask" :disabled="assigningTask"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="assigningTask" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ assigningTask ? 'Assigning...' : 'Assign Task' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ASSIGN TEMPLATE MODAL -->
    <div v-if="showTemplateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">playlist_add</span>
            Assign Template to {{ client.name }}
          </h3>
          <button @click="showTemplateModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <p class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Select a Template</p>
          <div v-if="loadingTemplates" class="flex justify-center py-8">
            <svg class="animate-spin h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <div v-else-if="templates.length === 0" class="text-center py-6 text-slate-400 text-sm">
            No templates found in SharePoint. Create one in Task Templates first.
          </div>
          <div v-else class="space-y-2 max-h-64 overflow-y-auto">
            <div v-for="tpl in templates" :key="tpl.id"
              @click="selectedTemplate = tpl"
              class="p-4 rounded-xl border-2 cursor-pointer transition-all"
              :class="selectedTemplate?.id === tpl.id ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'">
              <div class="flex items-center justify-between mb-1">
                <p class="text-sm font-semibold text-slate-800">{{ tpl.name }}</p>
                <span class="text-xs text-slate-400">{{ tpl.tasks.length }} tasks</span>
              </div>
              <p class="text-xs text-slate-500">{{ tpl.entityType }}</p>
              <p class="text-xs text-slate-400 mt-1 truncate">{{ tpl.tasks.map(t => t.title).join(' → ') }}</p>
            </div>
          </div>
          <div v-if="templateAssignError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{{ templateAssignError }}</div>
          <div v-if="templateAssignSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{{ templateAssignSuccess }}</div>
          <div class="flex gap-3 pt-2">
            <button @click="showTemplateModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button @click="assignTemplate" :disabled="!selectedTemplate || assigningTemplate"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="assigningTemplate" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ assigningTemplate ? 'Assigning...' : 'Assign Template' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- EDIT CLIENT MODAL -->
    <div v-if="showEditModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">edit</span>
            Edit Client Info
          </h3>
          <button @click="showEditModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
            <input v-model="editForm.name" type="text"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input v-model="editForm.email" type="email"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Phone</label>
              <input v-model="editForm.phone" type="tel"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Entity Type</label>
              <input v-model="editForm.serviceType" type="text" list="entityTypeListEdit"
                placeholder="Select or type an entity type"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white" />
              <datalist id="entityTypeListEdit">
                <option v-for="t in entityTypeOptions" :key="t" :value="t"></option>
              </datalist>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Tax Year</label>
              <select v-model="editForm.taxYear"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                <option v-for="y in taxYearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
          </div>
          <div v-if="editError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{{ editError }}</div>
          <div v-if="editSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{{ editSuccess }}</div>
          <div class="flex gap-3 pt-2">
            <button @click="showEditModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button @click="saveClientEdit" :disabled="savingEdit"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="savingEdit" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ savingEdit ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- UPLOAD DOCUMENT MODAL -->
    <div v-if="showUploadModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">upload_file</span>
            Upload Document
          </h3>
          <button @click="showUploadModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Destination Folder</label>
            <select v-model="uploadForm.folder"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
              <option v-for="f in folderNames" :key="f" :value="f">{{ f }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Select Files</label>
            <div
              class="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-primary/40 transition-colors"
              @click="$refs.uploadInput.click()">
              <span class="material-symbols-outlined text-slate-300" style="font-size: 36px;">cloud_upload</span>
              <p class="text-sm text-slate-500 mt-2">
                <span v-if="uploadForm.files.length === 0">Click to select files</span>
                <span v-else class="font-medium text-primary">{{ uploadForm.files.length }} file(s) selected</span>
              </p>
              <p v-if="uploadForm.files.length === 0" class="text-xs text-slate-400 mt-1">PDF, DOCX, XLSX, JPG, PNG</p>
              <ul v-else class="mt-2 text-xs text-slate-500 space-y-1">
                <li v-for="(f, i) in uploadForm.files" :key="i">{{ f.name }}</li>
              </ul>
            </div>
            <input ref="uploadInput" type="file" class="hidden" multiple @change="onFilesSelected" />
          </div>
          <div v-if="uploadError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">error</span>{{ uploadError }}
          </div>
          <div v-if="uploadSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>{{ uploadSuccess }}
          </div>
          <div v-if="uploadingDocs && uploadProgress.length" class="space-y-2">
            <div v-for="(p, i) in uploadProgress" :key="i" class="flex items-center gap-3">
              <span class="material-symbols-outlined text-slate-400" style="font-size: 16px;">insert_drive_file</span>
              <div class="flex-1">
                <p class="text-xs font-medium text-slate-700 truncate">{{ p.name }}</p>
                <div class="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div class="h-full rounded-full transition-all"
                    :class="p.done ? 'bg-green-500' : 'bg-primary'"
                    :style="{ width: p.done ? '100%' : '60%' }"></div>
                </div>
              </div>
              <span class="text-xs" :class="p.done ? 'text-green-600' : 'text-slate-400'">
                {{ p.done ? 'Done' : 'Uploading...' }}
              </span>
            </div>
          </div>
          <div class="flex gap-3 pt-2">
            <button @click="showUploadModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button @click="handleUpload" :disabled="uploadingDocs || uploadForm.files.length === 0"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="uploadingDocs" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span v-else class="material-symbols-outlined" style="font-size: 18px;">upload</span>
              {{ uploadingDocs ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SEND PORTAL INVITE MODAL -->
    <div v-if="showInviteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">mail</span>
            Send Portal Invite
          </h3>
          <button @click="showInviteModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <div class="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
            <p class="text-xs text-slate-400">Invite will be sent to</p>
            <p class="text-sm font-semibold text-slate-800">{{ client.name }}</p>
            <p class="text-sm text-slate-600">{{ client.email || 'No email on file' }}</p>
          </div>
          <p class="text-xs text-slate-400">
            This will create an invite record in SharePoint and mark the client's
            <span class="font-medium text-slate-600">Portal Access</span> as sent.
            The invite link expires in 7 days.
          </p>
          <div v-if="inviteError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">error</span>{{ inviteError }}
          </div>
          <div v-if="inviteSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>{{ inviteSuccess }}
          </div>
          <div class="flex gap-3 pt-2">
            <button @click="showInviteModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button @click="sendPortalInvite" :disabled="sendingInvite || !client.email"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="sendingInvite" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span v-else class="material-symbols-outlined" style="font-size: 18px;">send</span>
              {{ sendingInvite ? 'Sending...' : 'Send Invite' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import AppSidebar from '../components/AppSidebar.vue';

// Australian FY ends 30 June: before July we're still lodging the prior year
function buildTaxYearOptions() {
  const now = new Date();
  const currentFY = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return Array.from({ length: 4 }, (_, i) => String(currentFY - i));
}

const FALLBACK_ENTITY_TYPES = ['Individual', 'Sole Trader', 'Company',
  'Partnership', 'Trust', 'SMSF'];

export default {
  name: 'ClientDetailPage',
  components: { AppSidebar },

  data() {
    return {
      loading: true,
      loadingMessages: false,
      loadingDocs: false,
      activeTab: 'tasks',
      showStatusMenu: false,

      tabs: [
        { id: 'overview', label: 'Overview', icon: 'person' },
        { id: 'tasks', label: 'Tasks', icon: 'playlist_add_check' },
        { id: 'messages', label: 'Messages', icon: 'chat' },
        { id: 'documents', label: 'Documents', icon: 'folder_open' }
      ],

      statusOptions: ['New', 'Active', 'In Progress', 'Pending', 'Review Pending', 'Docs Missing', 'Filed', 'Completed'],

      client: {
        id: '', rawId: '', name: '', initials: '',
        entityType: '', status: '', statusClass: '', statusDotClass: '',
        cpaName: '', email: '', phone: '', address: '',
        taxId: '', taxYear: buildTaxYearOptions()[0], lastActive: ''
      },

      tasks: [],
      messages: [],

      folderNames: ['Uploaded Documents', 'Correspondence'],
      activeFolder: 'Uploaded Documents',
      folderStructure: {
        'Uploaded Documents': [],
        'Correspondence': []
      },

      // Quick Add Task
      addingTask: false,
      quickAddError: '',
      quickAddSuccess: '',
      newTask: { name: '', priority: '', dueDate: '' },

      // Send Message Modal
      showMessageModal: false,
      sendingMessage: false,
      messageError: '',
      messageSuccess: '',
      messageForm: { subject: '', message: '', sendEmail: false },

      // Assign Task Modal
      showAssignTaskModal: false,
      assigningTask: false,
      taskError: '',
      taskSuccess: '',
      taskForm: {
        title: '', description: '',
        category: 'Tax Preparation', priority: 'Medium', dueDate: ''
      },

      // Assign Template Modal
      showTemplateModal: false,
      loadingTemplates: false,
      assigningTemplate: false,
      templateAssignError: '',
      templateAssignSuccess: '',
      selectedTemplate: null,
      templates: [],

      // Edit Client Modal
      showEditModal: false,
      savingEdit: false,
      editError: '',
      editSuccess: '',
      editForm: { name: '', email: '', phone: '', serviceType: '', taxYear: '' },

      entityTypeOptions: FALLBACK_ENTITY_TYPES,

      // Inline per-field editing (info cards)
      editingField: null,
      fieldDraft: '',
      savingField: false,

      // Upload Modal
      showUploadModal: false,
      uploadingDocs: false,
      uploadError: '',
      uploadSuccess: '',
      uploadProgress: [],
      uploadForm: {
        folder: 'Uploaded Documents',
        files: []
      },

      // Portal Invite Modal
      showInviteModal: false,
      sendingInvite: false,
      inviteError: '',
      inviteSuccess: ''
    };
  },

  computed: {
    currentFolderFiles() {
      return this.folderStructure[this.activeFolder] || [];
    },

    taxYearOptions() {
      const years = buildTaxYearOptions();
      [this.editForm && this.editForm.taxYear, this.client && this.client.taxYear]
        .forEach(v => {
          if (v && !years.includes(String(v))) years.push(String(v));
        });
      years.sort((a, b) => b - a);
      return years;
    },

    currentTaxYear() {
      return buildTaxYearOptions()[0];
    }
  },

  async mounted() {
    await this.loadClientData();
    document.addEventListener('click', this.closeStatusMenu);

    try {
      const choices = await this.$sharepoint.getEntityTypeChoices();
      if (choices && choices.length) this.entityTypeOptions = choices;
    } catch (e) {
      console.error('Error loading entity type choices:', e);
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closeStatusMenu);
  },

  watch: {
    activeTab(tab) {
      if (tab === 'messages' && this.messages.length === 0) this.loadMessages();
      if (tab === 'documents') this.loadDocuments();
    }
  },

  methods: {
    // ===== DATA LOADING =====
    async loadClientData() {
      try {
        this.loading = true;
        const clientId = this.$route.params.id;
        const client = await this.$sharepoint.getClientById(clientId);

        if (client) {
          this.client = {
            rawId: client.Id,
            id: `#C-${client.Id}`,
            name: client.Title,
            initials: this.getInitials(client.Title),
            entityType: client.CompanyName || client.ServiceType || 'Individual',
            status: client.Status || 'New',
            statusClass: this.getStatusClass(client.Status),
            statusDotClass: this.getStatusDotClass(client.Status),
            cpaName: client.AuthorName || 'Unassigned',
            email: client.Email || '',
            phone: client.Phone || '',
            address: client.Address || '',
            taxId: client.TaxID || '',
            taxYear: client.TaxYear || this.currentTaxYear,
            lastActive: this.formatDate(client.Modified)
          };
        }
        await this.loadTasks(clientId);
      } catch (error) {
        console.error('Error loading client:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadTasks(clientId) {
  try {
    const allTasks = await this.$sharepoint.getClientTasks(clientId);
    const mapped = allTasks.map(task => ({
      id: task.Id,
      name: task.Title,
      category: task.Category || 'General',
      icon: this.getTaskIcon(task.Category),
      status: task.TaskStatus || 'Not Started',  // ✅ reads TaskStatus from SharePoint
      rawDueDate: task.DueDate,
      dueDate: task.DueDate ? new Date(task.DueDate).toLocaleDateString() : 'N/A',
      assignee: task.AssigneeName || 'Unassigned',
      assigneeInitials: this.getInitials(task.AssigneeName || 'UN'),
      priority: task.Priority || 'Normal',
      _completing: false  // ✅ needed for spinner guard
    }));
    // completed tasks go to bottom
    this.tasks = [
      ...mapped.filter(t => t.status !== 'Completed'),
      ...mapped.filter(t => t.status === 'Completed')
    ];
  } catch (error) {
    console.warn('Could not load tasks:', error.message);
    this.tasks = [];
  }
},


    async loadMessages() {
      if (!this.client.name) return;
      this.loadingMessages = true;
      try {
        const results = await this.$sharepoint.getClientMessages(this.client.name);
        this.messages = results.map(msg => ({
          id: msg.Id,
          subject: msg.Title || 'No Subject',
          content: msg.Message || '',
          sender: msg.Sender || 'Admin',
          senderInitials: this.getInitials(msg.Sender || 'Admin'),
          date: this.formatDate(msg.Created)
        }));
      } catch (error) {
        console.error('Error loading messages:', error);
        this.messages = [];
      } finally {
        this.loadingMessages = false;
      }
    },

    async loadDocuments() {
      if (!this.client.name) return;
      this.loadingDocs = true;
      try {
        const structure = await this.$sharepoint.getClientFolderStructure(this.client.name);
        this.folderStructure = {
          'Uploaded Documents': structure['Uploaded Documents'] || [],
          'Correspondence': structure['Correspondence'] || []
        };
      } catch (error) {
        console.error('Error loading documents:', error);
        this.folderStructure = { 'Uploaded Documents': [], 'Correspondence': [] };
      } finally {
        this.loadingDocs = false;
      }
    },

    // ===== SEND MESSAGE =====
    openSendMessage() {
      this.messageForm = { subject: '', message: '', sendEmail: false };
      this.messageError = '';
      this.messageSuccess = '';
      this.showMessageModal = true;
    },

    async sendMessage() {
      this.messageError = '';
      this.messageSuccess = '';
      if (!this.messageForm.subject.trim()) { this.messageError = 'Subject is required.'; return; }
      if (!this.messageForm.message.trim()) { this.messageError = 'Message cannot be empty.'; return; }
      this.sendingMessage = true;
      try {
        const user = this.$sharepoint.getCurrentUser();
        await this.$sharepoint.sendClientMessage({
          subject: this.messageForm.subject,
          message: this.messageForm.message,
          clientName: this.client.name,
          sender: user?.name || user?.username || 'Admin',
          sendEmail: this.messageForm.sendEmail
        });
        this.messageSuccess = '✅ Message sent successfully!';
        if (this.activeTab === 'messages') await this.loadMessages();
        setTimeout(() => { this.showMessageModal = false; }, 1500);
      } catch (error) {
        this.messageError = 'Failed to send message. Please try again.';
        console.error(error);
      } finally {
        this.sendingMessage = false;
      }
    },

    // ===== ASSIGN TASK =====
    openAssignTask() {
      this.taskForm = { title: '', description: '', category: 'Tax Preparation', priority: 'Medium', dueDate: '' };
      this.taskError = '';
      this.taskSuccess = '';
      this.showAssignTaskModal = true;
    },

    async assignTask() {
      this.taskError = '';
      this.taskSuccess = '';
      if (!this.taskForm.title.trim()) { this.taskError = 'Task title is required.'; return; }
      this.assigningTask = true;
      try {
        await this.$sharepoint.addTask({
          title: this.taskForm.title,
          description: this.taskForm.description,
          clientName: this.client.name,
          category: this.taskForm.category,
          priority: this.taskForm.priority,
          dueDate: this.taskForm.dueDate || null,
          status: 'To-Do'
        });
        this.taskSuccess = `✅ Task "${this.taskForm.title}" assigned!`;
        await this.loadTasks(this.client.rawId);
        setTimeout(() => { this.showAssignTaskModal = false; }, 1500);
      } catch (error) {
        this.taskError = 'Failed to assign task. Please try again.';
        console.error(error);
      } finally {
        this.assigningTask = false;
      }
    },

    // ===== QUICK ADD TASK =====
    async quickAddTask() {
      this.quickAddError = '';
      this.quickAddSuccess = '';
      if (!this.newTask.name.trim()) { this.quickAddError = 'Task name is required.'; return; }
      this.addingTask = true;
      try {
        await this.$sharepoint.addTask({
          title: this.newTask.name,
          clientName: this.client.name,
          category: 'General',
          priority: this.newTask.priority || 'Normal',
          dueDate: this.newTask.dueDate || null,
          status: 'To-Do'
        });
        this.quickAddSuccess = `✅ "${this.newTask.name}" added!`;
        this.newTask = { name: '', priority: '', dueDate: '' };
        await this.loadTasks(this.client.rawId);
        setTimeout(() => { this.quickAddSuccess = ''; }, 3000);
      } catch (error) {
        this.quickAddError = 'Failed to add task.';
        console.error(error);
      } finally {
        this.addingTask = false;
      }
    },

    // ===== ASSIGN TEMPLATE =====
    async openAssignTemplate() {
      this.selectedTemplate = null;
      this.templateAssignError = '';
      this.templateAssignSuccess = '';
      this.assigningTemplate = false;
      this.templates = [];
      this.showTemplateModal = true;
      try {
        this.loadingTemplates = true;
        this.templates = await this.$sharepoint.getTemplates();
      } catch (error) {
        console.error('Failed to load templates:', error);
        this.templateAssignError = 'Failed to load templates from SharePoint.';
      } finally {
        this.loadingTemplates = false;
      }
    },

    async assignTemplate() {
      if (!this.selectedTemplate) return;
      this.templateAssignError = '';
      this.templateAssignSuccess = '';
      this.assigningTemplate = true;
      try {
        const tasks = this.selectedTemplate.tasks.map(t => ({
          title: t.title,
          description: t.description || '',
          clientName: this.client.name,
          category: t.category || 'General',
          status: 'To-Do',
          priority: t.priority || 'Normal',
          dueDate: t.daysUntilDue
            ? new Date(Date.now() + t.daysUntilDue * 24 * 60 * 60 * 1000).toISOString()
            : null,
          templateId: this.selectedTemplate.id || 0
        }));
        await this.$sharepoint.addMultipleTasks(tasks);
        this.templateAssignSuccess = `✅ "${this.selectedTemplate.name}" assigned with ${tasks.length} tasks!`;
        await this.loadTasks(this.client.rawId);
        setTimeout(() => { this.showTemplateModal = false; }, 1800);
      } catch (error) {
        this.templateAssignError = 'Failed to assign template. Please try again.';
        console.error('Assign template error:', error);
      } finally {
        this.assigningTemplate = false;
      }
    },

    // ===== EDIT CLIENT =====
    openEditClient() {
      this.editForm = {
        name: this.client.name,
        email: this.client.email,
        phone: this.client.phone,
        serviceType: this.client.entityType,
        taxYear: this.client.taxYear
      };
      this.editError = '';
      this.editSuccess = '';
      this.showEditModal = true;
    },

    async saveClientEdit() {
      this.editError = '';
      this.editSuccess = '';
      if (!this.editForm.name.trim()) { this.editError = 'Name is required.'; return; }
      this.savingEdit = true;
      try {
        await this.$sharepoint.updateClient(this.client.rawId, this.editForm);
        this.client.name = this.editForm.name;
        this.client.initials = this.getInitials(this.editForm.name);
        this.client.email = this.editForm.email;
        this.client.phone = this.editForm.phone;
        this.client.entityType = this.editForm.serviceType;
        this.client.taxYear = this.editForm.taxYear;
        this.editSuccess = '✅ Client updated successfully!';
        setTimeout(() => { this.showEditModal = false; }, 1500);
      } catch (error) {
        this.editError = 'Failed to update client. Please try again.';
        console.error(error);
      } finally {
        this.savingEdit = false;
      }
    },

    // ===== INLINE FIELD EDIT =====
    startFieldEdit(field, currentValue) {
      this.editingField = field;
      this.fieldDraft = currentValue == null ? '' : String(currentValue);
    },

    cancelFieldEdit() {
      this.editingField = null;
      this.fieldDraft = '';
    },

    async saveField(field, updateKey) {
      this.savingField = true;
      try {
        await this.$sharepoint.updateClient(this.client.rawId, { [updateKey]: this.fieldDraft });
        this.client[field] = this.fieldDraft;
        this.editingField = null;
        this.fieldDraft = '';
      } catch (error) {
        // Leave the input open so the typed value isn't lost
        console.error('Failed to update ' + field + ':', error);
      } finally {
        this.savingField = false;
      }
    },

    // ===== UPDATE STATUS =====
    async updateStatus(newStatus) {
      this.showStatusMenu = false;
      const oldStatus = this.client.status;
      try {
        this.client.status = newStatus;
        this.client.statusClass = this.getStatusClass(newStatus);
        this.client.statusDotClass = this.getStatusDotClass(newStatus);
        await this.$sharepoint.updateClient(this.client.rawId, { status: newStatus });
      } catch (error) {
        this.client.status = oldStatus;
        this.client.statusClass = this.getStatusClass(oldStatus);
        this.client.statusDotClass = this.getStatusDotClass(oldStatus);
        console.error('Failed to update status:', error);
      }
    },

    closeStatusMenu(event) {
      if (!event.target.closest('.relative')) this.showStatusMenu = false;
    },

    // ===== TASK ACTIONS =====
   async markTaskComplete(task) {
  if (task._completing || task.status === 'Completed') return;

  const index = this.tasks.findIndex(t => t.id === task.id);
  if (index === -1) return;

  // Set spinner flag reactively
  this.tasks.splice(index, 1, { ...this.tasks[index], _completing: true });

  try {
    // ✅ writes TaskStatus = 'Completed' to SharePoint
    await this.$sharepoint.updateTaskStatus(task.id, 'Completed');

    // ✅ update reactively
    this.tasks.splice(index, 1, { ...this.tasks[index], status: 'Completed', _completing: false });

    // Re-sort: pending first, completed last
    this.tasks = [
      ...this.tasks.filter(t => t.status !== 'Completed'),
      ...this.tasks.filter(t => t.status === 'Completed')
    ];
  } catch (error) {
    console.error('Failed to mark task complete:', error);
    this.tasks.splice(index, 1, { ...this.tasks[index], _completing: false });
    alert('Failed to update task status. Please try again.');
  }
},

    async deleteTask(task) {
      if (!confirm(`Delete task "${task.name}"?`)) return;
      const taskId = task.Id || task.id;
      if (!taskId) { alert('Cannot delete task: missing SharePoint ID.'); return; }
      try {
        await this.$sharepoint.deleteTask(taskId);
      } catch (error) {
        console.error('Failed to delete task:', error);
        alert('Failed to delete task from SharePoint. Please try again.');
        return;
      }
      try {
        this.tasks = this.tasks.filter(t => (t.Id || t.id) !== taskId);
      } catch {
        await this.loadTasks(this.client.rawId);
      }
    },

    isOverdue(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },

    // ===== DOCUMENT UPLOAD =====
    openUploadModal() {
      this.uploadForm = { folder: this.activeFolder, files: [] };
      this.uploadError = '';
      this.uploadSuccess = '';
      this.uploadProgress = [];
      this.uploadingDocs = false;
      this.showUploadModal = true;
    },

    onFilesSelected(event) {
      this.uploadForm.files = Array.from(event.target.files);
      this.uploadError = '';
      this.uploadSuccess = '';
    },

    async handleUpload() {
      if (!this.uploadForm.files.length) return;
      this.uploadError = '';
      this.uploadSuccess = '';
      this.uploadingDocs = true;
      this.uploadProgress = this.uploadForm.files.map(f => ({ name: f.name, done: false }));

      try {
        for (let i = 0; i < this.uploadForm.files.length; i++) {
          const file = this.uploadForm.files[i];
          await this.$sharepoint.uploadDocument(file, {
            clientName: this.client.name,
            category: this.uploadForm.folder
          });
          this.uploadProgress[i].done = true;
        }
        this.uploadSuccess = `✅ ${this.uploadForm.files.length} file(s) uploaded to "${this.uploadForm.folder}"!`;
        await this.loadDocuments();
        this.activeFolder = this.uploadForm.folder;
        setTimeout(() => { this.showUploadModal = false; }, 1800);
      } catch (error) {
        this.uploadError = 'Upload failed. Please check permissions and try again.';
        console.error('Upload error:', error);
      } finally {
        this.uploadingDocs = false;
      }
    },

    async deleteDocument(doc) {
      if (!confirm(`Delete "${doc.Name}"?`)) return;
      try {
        await this.$sharepoint.deleteDocument(doc.FileUrl);
        this.folderStructure[this.activeFolder] = this.folderStructure[this.activeFolder]
          .filter(f => f.FileUrl !== doc.FileUrl);
      } catch (error) {
        console.error('Failed to delete document:', error);
        alert('Failed to delete document. Please try again.');
      }
    },

    // ===== PORTAL INVITE =====
    openInviteModal() {
      this.inviteError = '';
      this.inviteSuccess = '';
      this.showInviteModal = true;
    },

    async sendPortalInvite() {
      this.inviteError = '';
      this.inviteSuccess = '';
      if (!this.client.email) {
        this.inviteError = 'This client has no email address on file.';
        return;
      }
      this.sendingInvite = true;
      try {
        const user = this.$sharepoint.getCurrentUser();
        await this.$sharepoint.sendClientInvitation({
          clientName: this.client.name,
          email: this.client.email,
          sentBy: user?.name || user?.username || 'Admin'
        });
        await this.$sharepoint.updateClient(this.client.rawId, { portalAccessSent: true });
        this.inviteSuccess = `✅ Portal invite sent to ${this.client.email}!`;
        setTimeout(() => { this.showInviteModal = false; }, 1800);
      } catch (error) {
        this.inviteError = 'Failed to send invite. Please try again.';
        console.error('Invite error:', error);
      } finally {
        this.sendingInvite = false;
      }
    },

    // ===== HELPERS =====
    goToClientList() { this.$router.push('/admin/clients'); },

    getInitials(name) {
      if (!name) return '??';
      const parts = name.split(' ');
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },

    getTaskIcon(category) {
      const map = {
        'Tax Preparation': 'description', 'Document Collection': 'upload_file',
        'Compliance': 'verified', 'Accounting': 'calculate',
        'Review': 'rate_review', 'Filing': 'send', 'Signature': 'ink_pen'
      };
      return map[category] || 'task';
    },

    getFileType(filename) {
      if (!filename) return 'file';
      const ext = filename.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'pdf';
      if (['docx', 'doc'].includes(ext)) return 'word';
      if (['xlsx', 'xls'].includes(ext)) return 'excel';
      if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image';
      return 'file';
    },

    getDocIcon(type) {
      const map = { pdf: 'picture_as_pdf', word: 'description', excel: 'table_view', image: 'image', file: 'insert_drive_file' };
      return map[type] || 'insert_drive_file';
    },

    getDocBgClass(type) {
      const map = { pdf: 'bg-red-50', word: 'bg-blue-50', excel: 'bg-green-50', image: 'bg-purple-50', file: 'bg-slate-50' };
      return map[type] || 'bg-slate-50';
    },

    getDocIconClass(type) {
      const map = { pdf: 'text-red-500', word: 'text-blue-500', excel: 'text-green-500', image: 'text-purple-500', file: 'text-slate-500' };
      return map[type] || 'text-slate-500';
    },

    buildFileUrl(doc) {
      // Route through Netlify proxy with admin=1 bypass
      if (doc.DriveItemId && doc.DriveId) {
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(doc.DriveId)}&itemId=${encodeURIComponent(doc.DriveItemId)}&admin=1`;
      }
      if (doc.FileUrl) {
        return `/.netlify/functions/get-file?path=${encodeURIComponent(doc.FileUrl)}&admin=1`;
      }
      return '#';
    },

    formatFileSize(bytes) {
      if (!bytes) return 'N/A';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    getStatusClass(status) {
      const map = {
        'Active': 'bg-green-50 text-green-700 border-green-100',
        'Pending': 'bg-amber-50 text-amber-700 border-amber-100',
        'Review Pending': 'bg-amber-50 text-amber-700 border-amber-100',
        'Completed': 'bg-green-50 text-green-700 border-green-100',
        'Filed': 'bg-green-50 text-green-700 border-green-100',
        'In Progress': 'bg-blue-50 text-blue-700 border-blue-100',
        'Docs Missing': 'bg-red-50 text-red-700 border-red-100',
        'New': 'bg-slate-50 text-slate-600 border-slate-200'
      };
      return map[status] || 'bg-gray-50 text-gray-700 border-gray-100';
    },

    getStatusDotClass(status) {
      const map = {
        'Active': 'bg-green-500', 'Pending': 'bg-amber-500',
        'Review Pending': 'bg-amber-500', 'Completed': 'bg-green-500',
        'Filed': 'bg-green-500', 'In Progress': 'bg-blue-500',
        'Docs Missing': 'bg-red-500', 'New': 'bg-slate-400'
      };
      return map[status] || 'bg-gray-500';
    },

    getTaskStatusClass(status) {
      const map = {
        'Completed': 'bg-green-50 text-green-700 border-green-100',
        'In Progress': 'bg-blue-50 text-blue-700 border-blue-100',
        'Client Review': 'bg-purple-50 text-purple-700 border-purple-100',
        'Not Started': 'bg-slate-50 text-slate-600 border-slate-200',
        'To-Do': 'bg-slate-50 text-slate-600 border-slate-200',
        'Pending': 'bg-amber-50 text-amber-700 border-amber-100',
        'Blocked': 'bg-red-50 text-red-700 border-red-100'
      };
      return map[status] || 'bg-gray-50 text-gray-700 border-gray-100';
    },

    getTaskStatusDotClass(status) {
      const map = {
        'Completed': 'bg-green-500', 'In Progress': 'bg-blue-500',
        'Client Review': 'bg-purple-500', 'Not Started': 'bg-slate-400',
        'To-Do': 'bg-slate-400', 'Pending': 'bg-amber-500', 'Blocked': 'bg-red-500'
      };
      return map[status] || 'bg-gray-500';
    },

    getPriorityClass(priority) {
      const map = { 'High': 'text-red-600', 'Medium': 'text-amber-600', 'Normal': 'text-slate-500' };
      return map[priority] || 'text-slate-500';
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor(Math.abs(now - date) / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    }
  }
};
</script>